// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module GoogleAnalyticsProvider
 * @package @geenius/analytics
 * @description Implements the shared Google Analytics 4 provider using the
 * global `gtag.js` browser script. The provider injects the runtime script on
 * demand and adapts shared event/page payloads to GA4 conventions.
 */

import type { AnalyticsEvent, PageView } from "../types";
import {
  type AnalyticsProviderConfig,
  BaseAnalyticsProvider,
  type UserTraits,
} from "./interface";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

function getBrowserGlobals(): { doc: Document; win: Window } | null {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return null;
  }

  const activeDocument = document;
  const activeWindow = window;

  return { doc: activeDocument, win: activeWindow };
}

function getGtag(): Window["gtag"] | null {
  if (typeof window === "undefined") {
    return null;
  }

  const activeWindow = window;

  return typeof activeWindow.gtag === "function" ? activeWindow.gtag : null;
}

/**
 * Google Analytics 4 provider backed by the browser `gtag.js` runtime.
 */
export class GoogleAnalyticsProvider extends BaseAnalyticsProvider {
  readonly name = "google-analytics" as const;
  private initialized = false;

  private scriptSelector(measurementId: string): string {
    return `script[data-geenius-analytics-ga="${measurementId}"]`;
  }

  /**
   * Initializes GA4 by injecting the `gtag.js` script and configuring the property.
   *
   * @param config Provider configuration containing the GA measurement ID.
   * @returns Nothing.
   */
  init(config: AnalyticsProviderConfig): void {
    super.init(config);

    const globals = getBrowserGlobals();
    if (!globals) return;
    const { doc, win } = globals;

    if (!this.initialized) {
      if (!doc.head.querySelector(this.scriptSelector(config.apiKey))) {
        const script = doc.createElement("script");
        script.async = true;
        script.dataset.geeniusAnalyticsGa = config.apiKey;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${config.apiKey}`;
        doc.head.appendChild(script);
      }

      win.dataLayer = win.dataLayer || [];
      win.gtag ||= (...args: unknown[]): void => {
        win.dataLayer.push(args);
      };
      win.gtag("js", new Date());
      win.gtag("config", config.apiKey, {
        // The shared tracker owns page-view dispatch and privacy controls.
        send_page_view: false,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      this.initialized = true;
    }
  }

  /**
   * Sends a shared analytics event to GA4.
   *
   * @param event Shared analytics event payload.
   * @returns Nothing.
   */
  async trackEvent(event: AnalyticsEvent): Promise<void> {
    const gtag = getGtag();
    if (!gtag) return;

    gtag("event", event.name, {
      event_category: event.type,
      ...event.properties,
      page_location: event.url,
      page_referrer: event.referrer,
    });
  }

  /**
   * Sends a page-view payload to GA4.
   *
   * @param pageView Shared page-view payload.
   * @returns Nothing.
   */
  async trackPageView(pageView: PageView): Promise<void> {
    const gtag = getGtag();
    if (!gtag) return;

    gtag("event", "page_view", {
      ...pageView.properties,
      page_title: pageView.title,
      page_location: pageView.url,
      page_referrer: pageView.referrer,
    });
  }

  /**
   * Associates the current browser session with a user identifier and traits.
   *
   * @param userId Stable application user identifier.
   * @param traits Optional user properties forwarded to GA4.
   * @returns Nothing.
   */
  async identify(userId: string, traits?: UserTraits): Promise<void> {
    const gtag = getGtag();
    if (!gtag) return;

    gtag("set", { user_id: userId });
    if (traits) {
      gtag("set", "user_properties", traits);
    }
  }

  /**
   * Updates GA4 user properties for the current user association.
   *
   * @param _userId Stable application user identifier, already sent via identify.
   * @param traits User properties forwarded to GA4.
   * @returns Nothing.
   */
  async setUserProperties(_userId: string, traits: UserTraits): Promise<void> {
    const gtag = getGtag();
    if (!gtag) return;

    gtag("set", "user_properties", traits);
  }

  /**
   * Clears the current GA4 user association.
   *
   * @returns Nothing.
   */
  async reset(): Promise<void> {
    const gtag = getGtag();
    if (!gtag) return;
    gtag("set", { user_id: undefined });
  }
}
